import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { AuthService } from '../shared/services/auth.service';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { ApiService } from '../shared/services/api.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private isRefreshingComplaint = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private refreshTokenComplaintSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private toastr: ToastrService,
    private translate: TranslateService
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let authReq = request;
    const accessToken = this.authService.getToken() || this.authService.getTokenComplaint();
    if (accessToken && this.isAuthUrl(request.url)) {
      authReq = this.addTokenHeader(request, accessToken);
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && error.error.message === 'Token expired') {
          return this.handleTokenExpired(request, next);
        } else if (error.status === 401 && error.error.message === 'Token expired Complaint') {
          return this.handleTokenExpiredComplaint(request, next);
        }
        return throwError(error);
      })
    );
  }

  /**
   * Handles token expiration for normal authentication.
   */
  private handleTokenExpired(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      return this.apiService.refreshToken().pipe(
        switchMap((res: { message: string; accessToken: string }) => {
          this.isRefreshing = false;
          const newToken = res.accessToken;
          if (!newToken) throw new Error('No access token received');
          localStorage.setItem("access_token2", newToken);
          this.refreshTokenSubject.next(newToken);
          return this.apiService.meInfo().pipe(
            switchMap(userData => {
              this.authService.sendToken(
                userData.id_user,
                userData.n_user,
                userData.ape_user,
                userData.mail_user,
                userData.foto
              );
              return next.handle(this.addTokenHeader(request, newToken));
            })
          );
        }),
        catchError((refreshError) => {
          this.isRefreshing = false;
          if (refreshError.status === 401 &&
            refreshError.error.error === 'Error on refreshing tokens' ||
            refreshError.error.error === 'No refresh token provided in cookie') {
            this.ReloadComponent(() => {
              this.authService.logoutSimple();
            });
          }
          return throwError(refreshError);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => next.handle(this.addTokenHeader(request, token!)))
      );
    }
  }

  /**
   * Handles token expiration for complaint authentication.
   */
  private handleTokenExpiredComplaint(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshingComplaint) {
      this.isRefreshingComplaint = true;
      this.refreshTokenComplaintSubject.next(null);
      return this.apiService.refreshTokenComplaint().pipe(
        switchMap((res: any) => {
          this.isRefreshingComplaint = false;
          const newToken = res.accessTokenComplaint;
          if (!newToken) throw new Error('No access token received');
          localStorage.setItem("access_token_complaint", newToken);
          this.refreshTokenComplaintSubject.next(newToken);
          return next.handle(this.addTokenHeader(request, newToken));
        }),
        catchError((refreshError) => {
          this.isRefreshingComplaint = false;
          if (refreshError.status === 401 &&
            refreshError.error.error === 'Error on refreshing tokens' ||
            refreshError.error.error === 'No refresh token provided in cookie') {
            this.ReloadComponent(() => {
              this.authService.logoutSimpleComplaint();
            });
          }
          return throwError(refreshError);
        })
      );
    } else {
      return this.refreshTokenComplaintSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => next.handle(this.addTokenHeader(request, token!)))
      );
    }
  }

  private isAuthUrl(url: string): boolean {
    const withoutAuthPaths = ['login', 'refresh-token', 'restablecer-contrasenia'];
    return !withoutAuthPaths.some(path => url.includes(path));
  }

  private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
      withCredentials: true
    });
  }

  private ReloadComponent(callback?: () => void) {
    this.toastr.warning(
      this.translate.instant('texto_893'),
      this.translate.instant('texto_892'),
      {
        closeButton: true,
        progressBar: true,
        timeOut: 5000,
      }
    );

    setTimeout(() => {
      if (callback) callback();
      window.location.reload();
    }, 5000);
  }

}
